import React from 'react';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { motion } from 'framer-motion';
import setup from '../assets/data/setup.json';
import MotionSection from './ui/MotionSection';
import SectionHeader from './ui/SectionHeader';

type Testimonial = {
  company: string;
  companyUrl?: string;
  name: string;
  title?: string;
  linkedin?: string | null;
  companyLinkedin?: string;
  quote?: string | null;
  availability?: string;
};

function Testimonials() {
  const testimonials = (setup.testimonials || []) as Testimonial[];

  if (!testimonials.length) return null;

  return (
    <MotionSection id="testimonials" muted>
      <SectionHeader
        eyebrow="References"
        title="Testimonials & References"
        subtitle="Colleagues and clients I've worked with directly. Written testimonials coming soon—references available on request."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {testimonials.map((item, index) => (
          <motion.article
            key={`${item.company}-${item.name}`}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.08 }}
            className="ui-card p-6 flex flex-col h-full"
          >
            <FormatQuoteIcon sx={{ fontSize: 28 }} className="text-accent/40 mb-3" />

            {item.quote ? (
              <blockquote className="text-[15px] text-ink-secondary leading-relaxed mb-5 flex-1">
                &ldquo;{item.quote}&rdquo;
              </blockquote>
            ) : (
              <p className="text-sm text-ink-muted leading-relaxed mb-5 flex-1 italic">
                {item.availability || 'Written testimonial coming soon. Reference available on request.'}
              </p>
            )}

            <div className="pt-4 border-t border-line">
              <p className="font-semibold text-ink">{item.name}</p>
              <p className="text-sm text-ink-muted mt-0.5">
                {item.title ? `${item.title} · ` : ''}
                {item.companyUrl ? (
                  <a
                    href={item.companyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent hover:underline inline-flex items-center gap-0.5"
                  >
                    {item.company}
                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </a>
                ) : (
                  item.company
                )}
              </p>

              <div className="flex flex-wrap gap-2 mt-3">
                {item.linkedin && (
                  <a
                    href={item.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="ui-btn-secondary !px-3 !py-1.5 !text-xs inline-flex items-center gap-1.5"
                  >
                    <LinkedInIcon sx={{ fontSize: 14 }} />
                    LinkedIn Profile
                  </a>
                )}
                {!item.linkedin && item.companyLinkedin && (
                  <a
                    href={item.companyLinkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="ui-btn-secondary !px-3 !py-1.5 !text-xs inline-flex items-center gap-1.5"
                  >
                    <LinkedInIcon sx={{ fontSize: 14 }} />
                    {item.company} on LinkedIn
                  </a>
                )}
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </MotionSection>
  );
}

export default Testimonials;
